import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { selectDealerSelected } from 'app/pages/Header/selectors';
import { hasDealerContext } from 'utils/hasDealerContext';
import DataGridTypes from 'types/DataGridTypes';

import { useNurturingCandidatePickerExecutionLogFilterTray } from './useNurturingCandidatePickerExecutionLogFilterTray';

export type NurturingCandidateAuditBreadcrumb = {
  label: string;
  link?: string;
  isActive?: boolean;
};

export const useNurturingCandidateAuditPage = () => {
  const location = useLocation();
  const selectedDealer = useSelector(selectDealerSelected);
  const [isQuickFilterOpen, setIsQuickFilterOpen] = useState(false);

  const breadcrumbsList = useMemo((): NurturingCandidateAuditBreadcrumb[] => {
    const fromState = (
      location.state as {
        breadcrumbsList?: NurturingCandidateAuditBreadcrumb[];
      } | null
    )?.breadcrumbsList;
    if (fromState?.length) {
      return fromState;
    }
    return [{ label: 'Nurturing Candidate Audit', isActive: true }];
  }, [location.state]);

  const hasDealer = hasDealerContext(selectedDealer);
  const dealerObj =
    typeof selectedDealer === 'object' && selectedDealer !== null
      ? (selectedDealer as { site_title?: string; dealer?: string; _id?: string })
      : null;
  const dealerTitle = dealerObj?.site_title;
  const dealerId = dealerObj?.dealer || dealerObj?._id;

  const gridFilterFromState = useMemo(() => {
    const state = location.state as {
      run_id?: string;
      lead_id?: string;
      shopper_id?: string;
      action_id?: string;
    } | null;
    if (!state) return undefined;
    const { run_id, lead_id, shopper_id, action_id } = state;
    if (!run_id && !lead_id && !shopper_id && !action_id) return undefined;
    return { run_id, lead_id, shopper_id, action_id };
  }, [location.state]);

  const filterTray = useNurturingCandidatePickerExecutionLogFilterTray({
    dealerId: dealerId ? String(dealerId) : '',
    runId: gridFilterFromState?.run_id,
    fetchEnabled: isQuickFilterOpen,
  });

  const executionLogGridType = useMemo(() => {
    if (!dealerId) return null;
    return DataGridTypes.NURTURING_CANDIDATE_PICKER_EXECUTION_LOG(
      String(dealerId),
      gridFilterFromState,
      filterTray.executionLogFilterModel,
    );
  }, [dealerId, gridFilterFromState, filterTray.executionLogFilterModel]);

  return {
    breadcrumbsList,
    hasDealer,
    dealerTitle,
    dealerId,
    executionLogGridType,
    gridFilterFromState,
    isQuickFilterOpen,
    setIsQuickFilterOpen,
    filterTray,
  };
};
